import React, { useEffect, useState } from "react";
import { Medal, MessageSquare, Briefcase, Flag, Ticket, LogIn, ArrowRight, PartyPopper } from "lucide-react";
import { api } from "../../lib/api";
import { C, GLASS, IG_JAWSEL, glassInner, namaKota } from "../kota/ui";
import { KTW } from "./KTW";
import { TombolKTW, tombol } from "./TombolKTW";
import { kartu, Stepper, AntreanBar, Pendaftaran, Verifikasi, BuatPin, Masuk, LupaPin, LOKET_RESET } from "./LoketLangkah";

// Loket KTW — alur jadi warga: depan → Loket 1 daftar → Loket 2 kode dikirim via DM (polling) → Loket 3 PIN → Loket 4 KTW terbit.
// Permohonan aktif disimpan di sessionStorage agar muat ulang halaman tetap melanjutkan pemantauan.
const SIMPAN = "jawsel_permohonan";
const MANFAAT = [
  [Medal, "#d9a21b", "Gelar Warga Asli", "Lencana resmi di Kartu Warga & Pesan Warga"],
  [MessageSquare, "#2e9e5b", "Pesan Warga", "Suaramu tampil ke seluruh Jawsel dengan tanda terverifikasi"],
  [Briefcase, C.blue, "Kerja & Koin", "Lima profesi, kumpulkan koin dan naik level"],
  [Flag, "#8a56c8", "Misi Kota", "Ikut misi dari walikota, klaim hadiahnya"],
];
const CONTOH = { id: "00000000", nama: "Nama Kamu", ig_username: "username_kamu", created_at: new Date().toISOString() };

export function Loket({ kota, onSelesai }) {
  const [tahap, setTahap] = useState(() => (sessionStorage.getItem(SIMPAN) ? "verifikasi" : "depan"));
  const [perm, setPerm] = useState(() => { try { return JSON.parse(sessionStorage.getItem(SIMPAN)); } catch { return null; } });
  const [cities, setCities] = useState([]);
  const [sesi, setSesi] = useState(null); // {token, warga} setelah terverifikasi
  useEffect(() => { api.publicCities().then(d => setCities(d.cities || [])).catch(() => {}); }, []);
  const ajukan = p => { sessionStorage.setItem(SIMPAN, JSON.stringify(p)); setPerm(p); setTahap("verifikasi"); };
  const batal = () => { sessionStorage.removeItem(SIMPAN); setPerm(null); setTahap("depan"); };
  const reset = perm?.tujuan === "reset_pin";   // alur Lupa PIN: permohonan → verifikasi DM → PIN baru (tanpa PIN lama)
  const terverifikasi = (token, warga) => { sessionStorage.removeItem(SIMPAN); setSesi({ token, warga }); setTahap(reset ? "pin_baru" : warga?.pin_set ? "cetak" : "pin"); };

  if (tahap === "depan") return <Depan kota={kota} onAntre={() => setTahap("daftar")} onMasuk={() => setTahap("masuk")} />;
  if (tahap === "masuk") return <div style={kartu}><Masuk onMasuk={onSelesai} onKembali={() => setTahap("depan")} onLupa={() => setTahap("lupa")} /></div>;
  const langkah = { daftar: 0, lupa: 0, verifikasi: 1, pin: 2, pin_baru: 2, cetak: 3 }[tahap];
  return (
    <div data-testid="loket" style={kartu}>
      {perm && tahap !== "cetak" && <AntreanBar perm={perm} />}
      <Stepper aktif={langkah} langkah={reset || tahap === "lupa" ? LOKET_RESET : undefined} />
      {tahap === "daftar" && <Pendaftaran kota={kota} cities={cities} onAjukan={ajukan} onKembali={() => setTahap("depan")} />}
      {tahap === "lupa" && <LupaPin onAjukan={ajukan} onKembali={() => setTahap("masuk")} />}
      {tahap === "verifikasi" && perm && <Verifikasi perm={perm} onTerverifikasi={terverifikasi} onUlang={() => { sessionStorage.removeItem(SIMPAN); setPerm(null); setTahap(reset ? "lupa" : "daftar"); }} onBatal={batal} />}
      {tahap === "pin" && sesi && <BuatPin token={sesi.token} onSelesai={w => { setSesi(s => ({ ...s, warga: w })); setTahap("cetak"); }} />}
      {tahap === "pin_baru" && sesi && <BuatPin token={sesi.token} reset onSelesai={w => { setPerm(null); onSelesai(sesi.token, w); }} />}
      {tahap === "cetak" && sesi && <Cetak sesi={sesi} onSelesai={onSelesai} />}
    </div>
  );
}

function Depan({ kota, onAntre, onMasuk }) {
  return (
    <div data-testid="loket-depan" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={kartu}>
        <span data-testid="loket-dibuka" style={{ display: "inline-block", padding: "5px 12px", border: `2px solid ${C.blue}`, color: C.blue, borderRadius: 8, fontWeight: 900, fontSize: 11.5, letterSpacing: 1.5, transform: "rotate(-3deg)", background: "rgba(255,255,255,.55)" }}>LOKET DIBUKA</span>
        <h2 style={{ margin: "12px 0 4px", fontSize: 24, fontWeight: 800, letterSpacing: -0.5 }}>Jadilah Warga Asli Jawsel</h2>
        <p style={{ margin: "0 0 14px", fontSize: 13.5, color: GLASS.sub, lineHeight: 1.5 }}>Urus Kartu Warga Jawsel (KTW) dalam ±1 menit. Gratis, cukup sekali seumur hidup{kota ? ` — kamu akan tercatat sebagai warga Kota ${namaKota(kota)}` : ""}.</p>
        <KTW w={{ ...CONTOH, city_id: kota || "jawsel" }} contoh />
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 14 }}>
          {MANFAAT.map(([Icon, warna, j, s]) => <div key={j} data-testid="loket-manfaat" style={{ ...glassInner(false), borderRadius: 14, padding: "10px 12px", display: "flex", gap: 12, alignItems: "center" }}>
            <span style={{ width: 34, height: 34, borderRadius: 99, background: warna, display: "grid", placeItems: "center", flex: "none" }}><Icon size={15} color="#fff" /></span>
            <div style={{ lineHeight: 1.3 }}><b style={{ fontSize: 13.5 }}>{j}</b><div style={{ fontSize: 12, color: GLASS.sub }}>{s}</div></div>
          </div>)}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 14 }}>
          <button data-testid="loket-antre" onClick={onAntre} style={tombol(true)}><Ticket size={16} /> Ambil nomor antrean</button>
          <button data-testid="loket-masuk" onClick={onMasuk} style={tombol(false)}><LogIn size={15} /> Sudah punya KTW? Masuk</button>
        </div>
      </div>
      <div data-testid="loket-syarat" style={{ ...kartu, border: "1px dashed var(--g-handle)", fontSize: 13, lineHeight: 1.6 }}>
        <b>Persyaratan</b>
        <ol style={{ margin: "6px 0 0", paddingLeft: 20, color: GLASS.sub }}>
          <li>Sudah mengikuti <b style={{ color: GLASS.ink }}>{IG_JAWSEL}</b> di Instagram.</li>
          <li>Bisa mengirim DM dari akun Instagram-mu sendiri.</li>
          <li>Tanpa biaya. <b style={{ color: GLASS.ink }}>Petugas tidak pernah meminta password Instagram-mu.</b></li>
        </ol>
      </div>
    </div>
  );
}

function Cetak({ sesi, onSelesai }) {
  const w = sesi.warga;
  return (
    <div data-testid="loket-cetak" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div><h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, display: "flex", alignItems: "center", gap: 8 }}><PartyPopper size={18} color="#d9a21b" /> KTW-mu sudah jadi!</h3>
        <p style={{ margin: "4px 0 0", fontSize: 13, color: GLASS.sub, lineHeight: 1.5 }}>Selamat, <b style={{ color: GLASS.ink }}>@{w.ig_username}</b> resmi menjadi Warga Asli Kota {namaKota(w.city_id)}.</p></div>
      <KTW w={w} lv={1} />
      <TombolKTW w={w} lv={1} />
      <button data-testid="loket-ke-balai" onClick={() => onSelesai(sesi.token, w)} style={tombol(false)}>Masuk ke Balai saya <ArrowRight size={15} /></button>
    </div>
  );
}
